import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  StyledRowInputValue,
  Text,
  Column,
  RowValue,
  Value,
  StyledRowInput,
  Relative,
  DropDownMenu,
  DropDownValue,
  Chevron,
  Attributes,
  DropDown,
  MessageContainer,
  MessageTitle,
  MessageSubtitle,
  SuccessImg,
  StyledDropDownMenu,
  BackContainer,
  ChevronLeft,
} from './ApproveProductPage.styles.js';

import openToast from '../../utils/toast.js';

import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import Checkbox from '../../components/Checkbox/Checkbox.js';

import Success from '../../assets/images/success.png';
import { ReactComponent as Loading } from '../../assets/icons/loader-white.svg';

import { selectApproveProductPending, selectCategories, selectMarketplaceCategories, selectSections } from '../../store/slices/partners/slice.js';
import { approveProduct, fetchCategories, fetchMarketplaceCategories, fetchSections } from '../../store/slices/partners/asyncThunks.js';

const ApproveProductPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const id = params.id;
  const toastId = useRef(null);
  const [tab, setTab] = useState(0);
  const [openSection, setOpenSection] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [openMarketplaceCategory, setOpenMarketplaceCategory] = useState(false);

  const [isMobileApp, setIsMobileApp] = useState(false);
  const [isWebApp, setIsWebApp] = useState(false);
  const [webCategories, setWebCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);

  const allCategories = useSelector(selectCategories);
  const allSections = useSelector(selectSections);
  const marketplaceCategories = useSelector(selectMarketplaceCategories);
  const approveProductPending = useSelector(selectApproveProductPending);

  useEffect(() => {
    dispatch(fetchCategories())
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
    dispatch(fetchMarketplaceCategories())
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
    dispatch(fetchSections())
      .unwrap()
      .then(() => {})
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
  }, [dispatch]);

  const handleSectionClicked = section => {
    setSections([section]);
    setOpenSection(false);
  };

  const handleCategoryClicked = category => {
    setCategories([category]);
    setOpenCategory(false);
  };

  const handleMarketplaceCategoryClicked = category => {
    setWebCategories([category]);
    setOpenMarketplaceCategory(false);
  };

  const handleApproveProduct = () => {
    const body = {
      isMobileApp,
      isWebApp,
      ...(isMobileApp && { sections: sections.map(section => section.id) }),
      ...(isMobileApp && {
        categories: categories.map(category => category.id),
      }),
      ...(isWebApp && {
        webCategories: webCategories.map(category => category.id),
      }),
    };

    dispatch(approveProduct({ id, body }))
      .unwrap()
      .then(() => {
        setTab(1);
      })
      .catch(err => openToast(toastId, 'error-fetching', 'error', err.message));
  };

  return (
    <MainContainer pageTitle={'products'}>
      <BackContainer onClick={() => navigate(-1)}>
        <ChevronLeft />
        {t('back')}
      </BackContainer>
      {tab === 0 && (
        <Column>
          <StyledRowInputValue $opacity>
            <Text>{t('product')}:</Text>
            <Column>
              <RowValue>
                <Value>{t('user-app')}</Value>
                <Checkbox
                  checked={isMobileApp}
                  onChange={() => {
                    setIsMobileApp(!isMobileApp);
                  }}
                />
              </RowValue>
              <RowValue>
                <Value>{t('b2b-web-app')}</Value>
                <Checkbox
                  checked={isWebApp}
                  onChange={() => {
                    setIsWebApp(!isWebApp);
                  }}
                />
              </RowValue>
            </Column>
          </StyledRowInputValue>
          <StyledRowInput $opacity={isMobileApp}>
            <Text>{t('user-app-section')}:</Text>
            <Relative>
              <DropDown
                $open={openSection}
                onClick={() => {
                  if (isMobileApp) {
                    setOpenSection(!openSection);
                    setOpenCategory(false);
                    setOpenMarketplaceCategory(false);
                  }
                }}>
                <DropDownValue>{!sections.length ? t('sections') : t(sections[0]?.label)}</DropDownValue>
                <Chevron $open={openSection} />
              </DropDown>
              {openSection && (
                <DropDownMenu>
                  <StyledDropDownMenu>
                    {allSections.map((section, index) => (
                      <Attributes key={index} onClick={() => handleSectionClicked(section)}>
                        {t(section.label)}
                      </Attributes>
                    ))}
                  </StyledDropDownMenu>
                </DropDownMenu>
              )}
            </Relative>
          </StyledRowInput>
          <StyledRowInput $opacity={isMobileApp}>
            <Text>{t('user-app-category')}:</Text>
            <Relative>
              <DropDown
                $open={openCategory}
                onClick={() => {
                  if (isMobileApp) {
                    setOpenCategory(!openCategory);
                    setOpenMarketplaceCategory(false);
                    setOpenSection(false);
                  }
                }}>
                <DropDownValue>{!categories.length ? t('category') : t(categories[0]?.name)}</DropDownValue>
                <Chevron $open={openCategory} />
              </DropDown>
              {openCategory && (
                <DropDownMenu>
                  <StyledDropDownMenu>
                    {allCategories.map((category, index) => (
                      <Attributes key={index} onClick={() => handleCategoryClicked(category)}>
                        {t(category.name)}
                      </Attributes>
                    ))}
                  </StyledDropDownMenu>
                </DropDownMenu>
              )}
            </Relative>
          </StyledRowInput>

          <StyledRowInput $opacity={isWebApp}>
            <Text>{t('web-app-category')}:</Text>
            <Relative>
              <DropDown
                $open={openMarketplaceCategory}
                onClick={() => {
                  if (isWebApp) {
                    setOpenMarketplaceCategory(!openMarketplaceCategory);
                    setOpenCategory(false);
                    setOpenSection(false);
                  }
                }}>
                <DropDownValue>{!webCategories.length ? t('marketplace-category') : t(webCategories[0]?.name)}</DropDownValue>
                <Chevron $open={openMarketplaceCategory} />
              </DropDown>
              {openMarketplaceCategory && (
                <DropDownMenu>
                  <StyledDropDownMenu>
                    {marketplaceCategories.map((category, index) => (
                      <Attributes key={index} onClick={() => handleMarketplaceCategoryClicked(category)}>
                        {t(category.name)}
                      </Attributes>
                    ))}
                  </StyledDropDownMenu>
                </DropDownMenu>
              )}
            </Relative>
          </StyledRowInput>
          <Button
            onClick={() => {
              handleApproveProduct();
            }}>
            {approveProductPending ? <Loading height={20} width={20} /> : t('confirm')}
          </Button>
        </Column>
      )}

      {tab === 1 && (
        <MessageContainer>
          <MessageTitle>{t('product-approved')}</MessageTitle>
          <MessageSubtitle>{t('product-is-live-now')}</MessageSubtitle>
          <SuccessImg src={Success} />
          <Button onClick={() => navigate('/products?page=1')}>{t('go-to-products-list')}</Button>
        </MessageContainer>
      )}
    </MainContainer>
  );
};

export default ApproveProductPage;
